import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import styled from '@emotion/styled';
import Modal from 'components/modal';
import Button from 'components/button/button';
import { AppUI } from '@seedlang/state';
import { Theme } from '@seedlang/constants';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 10px 14px;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  text-align: center;
`;

const TermsLink = styled.a`
  color: ${Theme.blue};
  font-size: 16px;
  text-decoration: underline;
`;

@observer
class UpdatedTermsModal extends React.Component {
  @observable saving = false;
  @observable acceptedLocally = false;

  componentDidMount() {
    this.syncLocalAcceptanceState();
  }

  componentDidUpdate() {
    this.syncLocalAcceptanceState();
  }

  get userGroupId() {
    return AppUI.user.currentUserGroup?.id;
  }

  get localAcceptanceStorageKey() {
    return this.userGroupId ? `updated-terms-accepted:${this.userGroupId}` : null;
  }

  get persistedLocalAcceptance() {
    if (!this.localAcceptanceStorageKey || typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    try {
      return window.localStorage.getItem(this.localAcceptanceStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  setPersistedLocalAcceptance = (value) => {
    if (!this.localAcceptanceStorageKey || typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      if (value) {
        window.localStorage.setItem(this.localAcceptanceStorageKey, 'true');
      } else {
        window.localStorage.removeItem(this.localAcceptanceStorageKey);
      }
    } catch (error) {
      // Ignore localStorage failures and rely on in-memory state.
    }
  }

  syncLocalAcceptanceState = () => {
    if (!this.userGroupId) {
      if (this.acceptedLocally) {
        this.acceptedLocally = false;
      }
      return;
    }

    if (AppUI.user.currentUserGroup?.userAgreedToTerms) {
      this.setPersistedLocalAcceptance(false);
      if (this.acceptedLocally) {
        this.acceptedLocally = false;
      }
      return;
    }

    if (this.persistedLocalAcceptance && !this.acceptedLocally) {
      this.acceptedLocally = true;
      this.persistAcceptanceToServer();
    }
  }

  persistAcceptanceToServer = () => {
    if (!this.userGroupId) {
      this.saving = false;
      return;
    }

    AppUI.userGroupStore.update({
      ids: { userGroupId: this.userGroupId },
      data: { user_agreed_to_terms: true },
      failureCallback: () => {
        this.saving = false;
      },
    }, () => {
      AppUI.loadUser(null, {
        forceRefresh: true,
        successCallback: () => {
          this.saving = false;
        },
        failureCallback: () => {
          this.saving = false;
        },
      });
    });
  }

  @computed get showModal() {
    const routeName = AppUI.routeStore.routeName;
    return AppUI.siteIsSeedlang
      && !AppUI.user.signedOut
      && !!AppUI.user.currentUserGroup?.id
      && ['home', 'reviews.home'].includes(routeName)
      && !this.acceptedLocally
      && !AppUI.user.currentUserGroup?.userAgreedToTerms;
  }

  onAccept = () => {
    if (this.saving || !this.userGroupId) {
      return;
    }

    this.acceptedLocally = true;
    this.setPersistedLocalAcceptance(true);
    this.saving = true;
    this.persistAcceptanceToServer();
  }

  render() {
    if (!this.showModal) {
      return <span />;
    }

    return (
      <Modal
        width={AppUI.layout.isMobile ? '90%' : '440px'}
        height='fit-content'
        top='120px'
        modalBackground='rgba(0, 0, 0, 0.85)'
        title='Terms of Service Updated'
        gradient
      >
        <Wrapper>
          <Description>
            We&apos;ve made updates to our <TermsLink href='/terms' target='_blank' rel='noopener noreferrer'>Terms of Service</TermsLink>. Please review and accept the new terms to continue using the website.
          </Description>
          <Button
            onClick={this.onAccept}
            disabled={this.saving}
            width='100%'
            height='50px'
            borderRadius='50px'
            background={Theme.blue}
            backgroundOnHover={Theme.blue}
            backgroundOnActive={Theme.blue}
            scaleOnHover={1}
          >
            {this.saving ? 'Saving...' : 'Accept Terms of Service'}
          </Button>
        </Wrapper>
      </Modal>
    );
  }
}

export default UpdatedTermsModal;
